import type { LeaderboardProfile, LeaderboardRecord } from './leaderboard';

const SUPABASE_PUBLIC_URL = (import.meta.env.VITE_SUPABASE_URL || '').replace(
  /\/$/,
  ''
);

export const DEFAULT_LEADERBOARD_AVATAR = '/pictures/logo-v4.png';

export function buildLeaderboardProfilePictureUrl(
  name: string | null | undefined
): string | null {
  if (!name) return null;
  const trimmed = String(name).trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (!SUPABASE_PUBLIC_URL) return null;

  const sanitized = trimmed.replace(/^\/+/, '');
  if (!sanitized) return null;

  return `${SUPABASE_PUBLIC_URL}/storage/v1/object/public/${encodeURI(
    sanitized
  )}`;
}

export interface LeaderboardDisplayRow {
  userId?: string;
  rank: number;
  name: string;
  totalMinutes: number;
  totalSeconds?: number;
  sessions: number;
  dayStreak?: number;
  profilePictureUrl?: string | null;
  profileIsPrivate?: boolean;
  profileSlug?: string | null;
}

interface BuildLeaderboardDisplayEntriesOptions {
  entries: LeaderboardRecord[];
  profiles: Record<string, LeaderboardProfile>;
  leaderboardTab: 'minutes' | 'streak';
  currentUserId: string | null;
  limit?: number;
}

export function buildLeaderboardDisplayEntries({
  entries,
  profiles,
  leaderboardTab,
  currentUserId,
  limit,
}: BuildLeaderboardDisplayEntriesOptions): LeaderboardDisplayRow[] {
  if (!entries || entries.length === 0) return [];

  const sorted = [...entries]
    .filter(
      (entry) =>
        Number(entry.total_duration) > 0 || Number(entry.current_streak) > 0
    )
    .sort((a, b) => {
      if (leaderboardTab === 'minutes') {
        const aMinutes = Math.floor((Number(a.total_duration) || 0) / 60);
        const bMinutes = Math.floor((Number(b.total_duration) || 0) / 60);
        if (bMinutes !== aMinutes) return bMinutes - aMinutes;
        const aSessions = Number(a.total_sessions) || 0;
        const bSessions = Number(b.total_sessions) || 0;
        if (bSessions !== aSessions) return bSessions - aSessions;
        return (
          (Number(b.total_duration) || 0) - (Number(a.total_duration) || 0)
        );
      }
      const aStreak = Number(a.current_streak) || 0;
      const bStreak = Number(b.current_streak) || 0;
      if (bStreak !== aStreak) return bStreak - aStreak;
      const aDuration = Number(a.total_duration) || 0;
      const bDuration = Number(b.total_duration) || 0;
      if (bDuration !== aDuration) return bDuration - aDuration;
      return (Number(b.total_sessions) || 0) - (Number(a.total_sessions) || 0);
    });

  const slice = typeof limit === 'number' ? sorted.slice(0, limit) : sorted;

  // build ranking (competition-style) for the current tab so ties share ranks
  const rows: LeaderboardDisplayRow[] = [];
  let lastRankKey: number | null = null;
  let lastAssignedRank = 0;

  slice.forEach((entry, index) => {
    const key =
      leaderboardTab === 'minutes'
        ? Math.floor((Number(entry.total_duration) || 0) / 60)
        : Number(entry.current_streak) || 0;

    const assignedRank =
      lastRankKey !== null && key === lastRankKey
        ? lastAssignedRank
        : index + 1;

    lastRankKey = key;
    lastAssignedRank = assignedRank;

    const profile = entry.user_id ? profiles[entry.user_id] || null : null;
    const isProfileHidden = profile?.display_profile === false;
    const profilePictureUrl = isProfileHidden
      ? DEFAULT_LEADERBOARD_AVATAR
      : buildLeaderboardProfilePictureUrl(profile?.profile_picture_name);
    const profileName = isProfileHidden
      ? 'PrivateBreather'
      : (profile?.display_name || profile?.profile_name || '').trim();
    const fallbackSuffix = entry.user_id
      ? entry.user_id
          .replace(/[^a-zA-Z0-9]/g, '')
          .slice(0, 6)
          .toUpperCase()
      : String(index + 1);
    const fallbackName = `Breather ${fallbackSuffix}`;
    const baseName = profileName || fallbackName;
    const resolvedName =
      currentUserId && entry.user_id === currentUserId
        ? `${baseName} (You)`
        : baseName;

    const rawSeconds = Number(entry.total_duration) || 0;
    const hasPublicProfile = profile?.display_profile === true;
    const rawProfileSlug = (profile?.profile_name || '').trim();
    const profileSlug =
      hasPublicProfile && rawProfileSlug ? rawProfileSlug : null;

    rows.push({
      userId: entry.user_id,
      rank: assignedRank,
      name: resolvedName,
      totalMinutes: Math.floor(rawSeconds / 60),
      totalSeconds: rawSeconds,
      sessions: Number(entry.total_sessions) || 0,
      dayStreak: Number(entry.current_streak) || 0,
      profilePictureUrl,
      profileIsPrivate: isProfileHidden,
      profileSlug,
    });
  });

  return rows;
}
